import {Component, computed, DestroyRef, effect, inject, OnInit, signal} from '@angular/core';
import {CommonModule} from '@angular/common';
import {ProjectBaseStepComponent} from '../../../../project-base-step/project-base-step.component';
import {DemonstrationAgenda, DirectusService, DirectusTranslationPipe} from '@frontend/directus';
import {
  DemonstrationAgendaItemResponse,
  DemonstrationControllerService,
  DemonstrationUseCaseResponse,
} from '@frontend/shared-api';
import {takeUntilDestroyed, toSignal} from '@angular/core/rxjs-interop';
import {finalize} from 'rxjs';
import {ToastService} from '@frontend/toast';
import {BadgeModule} from 'primeng/badge';
import {LoadingComponent} from '../../../../../../../shared/components/loading/loading.component';
import {buildUseCaseTranslations} from '../../../../../../../shared/utils/use-case-translations';
import {TranslocoDirective} from '@jsverse/transloco';

interface SummaryUseCase {
  generatedUseCaseId: number;
  translations: any[];
  duration: number;
  sortOrder: number;
}

interface SummaryAgendaItem {
  directusId: number;
  translations: any[];
  orderNumber: number;
  duration: number;
  useCases: SummaryUseCase[];
  abbreviation: string | null;
}

@Component({
  selector: 'app-step-9-3-rfd-agenda-summary',
  imports: [
    CommonModule,
    DirectusTranslationPipe,
    BadgeModule,
    LoadingComponent,
    TranslocoDirective,
  ],
  templateUrl: './step-7-3-rfd-agenda-summary.component.html',
  styles: [
    `
      .total-duration-badge {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 2.5rem;
        height: 2.5rem;
        border-radius: 0.5rem;
        color: white;
        font-size: 1.25rem;
        font-weight: 600;
        box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05);
        background-color: rgb(255, 79, 102);
      }
    `,
  ],
})
export class Step73RfdAgendaSummaryComponent extends ProjectBaseStepComponent implements OnInit {
  private readonly directusService = inject(DirectusService);
  private readonly demonstrationService = inject(DemonstrationControllerService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly toast = inject(ToastService);

  protected readonly loading = signal(true);
  protected readonly directusAgendaItems = signal<DemonstrationAgenda[]>([]);
  protected readonly savedAgenda = signal<DemonstrationAgendaItemResponse[]>([]);
  protected readonly savedUseCases = signal<DemonstrationUseCaseResponse[]>([]);
  protected readonly summaryItems = signal<SummaryAgendaItem[]>([]);

  // Track collapsed items. If an item ID is in this set, it is collapsed.
  // Initially empty means all are expanded.
  protected readonly collapsedItems = signal<Set<number>>(new Set());

  // Track expanded use case IDs for descriptions
  protected readonly expandedUseCaseIds = signal<Set<number>>(new Set());

  protected readonly currentLang = toSignal(this.translocoService.langChanges$, {
    initialValue: this.translocoService.getActiveLang(),
  });

  protected readonly totalDuration = computed(() => {
    return this.summaryItems().reduce((sum, item) => sum + item.duration, 0);
  });

  constructor() {
    super();

    effect(() => {
      const directusItems = this.directusAgendaItems();
      const savedAgendaItems = this.savedAgenda();
      const savedUseCaseItems = this.savedUseCases();

      if (directusItems.length === 0 || savedAgendaItems.length === 0) return;

      this.buildSummary(directusItems, savedAgendaItems, savedUseCaseItems);
    });
  }

  override ngOnInit() {
    super.ngOnInit();
    this.loadData();
  }

  protected toggleCollapse(directusId: number): void {
    this.collapsedItems.update((current) => {
      const next = new Set(current);
      if (next.has(directusId)) {
        next.delete(directusId);
      } else {
        next.add(directusId);
      }
      return next;
    });
  }

  protected isCollapsed(directusId: number): boolean {
    return this.collapsedItems().has(directusId);
  }

  protected toggleUseCaseDescription(generatedUseCaseId: number, event: Event): void {
    event.stopPropagation();
    this.expandedUseCaseIds.update((current) => {
      const next = new Set(current);
      if (next.has(generatedUseCaseId)) {
        next.delete(generatedUseCaseId);
      } else {
        next.add(generatedUseCaseId);
      }
      return next;
    });
  }

  protected isUseCaseExpanded(generatedUseCaseId: number): boolean {
    return this.expandedUseCaseIds().has(generatedUseCaseId);
  }

  protected formatDuration(minutes: number): string {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}:${mins.toString().padStart(2, '0')} h`;
    }
    return `${minutes} min`;
  }

  private loadData() {
    this.loading.set(true);

    this.directusService
      .loadDemonstrationAgenda()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.loading.set(false)),
      )
      .subscribe({
        next: (directusData) => {
          this.directusAgendaItems.set(directusData);
          this.loadSavedAgenda();
        },
        error: (error) => {
          console.error('Error loading agenda:', error);
          this.toast.showError('common.error', 'error.generic.detail');
        },
      });
  }

  private loadSavedAgenda() {
    this.demonstrationService
      .getAgenda({projectId: this.projectId})
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (savedData) => {
          this.savedAgenda.set(savedData);
          this.loadSavedUsecases();
        },
        error: () => this.savedAgenda.set([]),
      });
  }

  private loadSavedUsecases() {
    this.demonstrationService
      .getUseCases({projectId: this.projectId})
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (useCases) => this.savedUseCases.set(useCases),
        error: () => this.savedUseCases.set([]),
      });
  }

  private buildSummary(
    directusItems: DemonstrationAgenda[],
    savedAgendaItems: DemonstrationAgendaItemResponse[],
    savedUseCaseItems: DemonstrationUseCaseResponse[],
  ) {
    const sortedAgendaItems = this.sortAgendaItems(savedAgendaItems);

    const summary = sortedAgendaItems
      .map((savedAgenda) => this.buildSummaryItem(directusItems, savedAgenda, savedUseCaseItems))
      .filter((item): item is SummaryAgendaItem => item !== null);

    this.summaryItems.set(summary);
  }

  private sortAgendaItems(
    items: DemonstrationAgendaItemResponse[],
  ): DemonstrationAgendaItemResponse[] {
    return [...items].sort((a, b) => (a.orderNumber ?? 0) - (b.orderNumber ?? 0));
  }

  private buildSummaryItem(
    directusItems: DemonstrationAgenda[],
    savedAgenda: DemonstrationAgendaItemResponse,
    savedUseCaseItems: DemonstrationUseCaseResponse[],
  ): SummaryAgendaItem | null {
    const directusAgenda = directusItems.find(
      (d) => d.id === savedAgenda.demonstrationAgendaDirectusId,
    );
    if (!directusAgenda) return null;

    const includedUseCases = this.getIncludedUseCases(savedAgenda, savedUseCaseItems);

    // If no use cases are included, only show this agenda topic if explicitly enabled in Directus
    // and it has a duration (selected in step-9-1) > 0.
    if (includedUseCases.length === 0) {
      if (!this.shouldDisplayWithoutUseCases(directusAgenda, savedAgenda)) {
        return null;
      }

      return {
        directusId: directusAgenda.id,
        translations: directusAgenda.translations,
        orderNumber: savedAgenda.orderNumber ?? 0,
        duration: savedAgenda.duration ?? 0,
        useCases: [],
        abbreviation: directusAgenda.abbreviation || null,
      };
    }

    const actualDuration = includedUseCases.reduce((sum, uc) => sum + uc.duration, 0);

    return {
      directusId: directusAgenda.id,
      translations: directusAgenda.translations,
      orderNumber: savedAgenda.orderNumber ?? 0,
      duration: actualDuration,
      useCases: includedUseCases,
      abbreviation: directusAgenda.abbreviation || null,
    };
  }

  private shouldDisplayWithoutUseCases(
    directusAgenda: DemonstrationAgenda,
    savedAgenda: DemonstrationAgendaItemResponse,
  ): boolean {
    const duration = savedAgenda.duration ?? 0;
    return directusAgenda.display_without_usecases === true && duration > 0;
  }

  private getIncludedUseCases(
    savedAgenda: DemonstrationAgendaItemResponse,
    savedUseCaseItems: DemonstrationUseCaseResponse[],
  ): SummaryUseCase[] {
    const savedIncludedUseCasesForAgenda = savedUseCaseItems.filter(
      (s) => s.demonstrationAgendaItemId === savedAgenda.id && s.included === true,
    );
    if (savedIncludedUseCasesForAgenda.length === 0) return [];

    return savedIncludedUseCasesForAgenda
      .map((savedU) => ({
        generatedUseCaseId: savedU.generatedUseCaseId ?? 0,
        translations: buildUseCaseTranslations(
          savedU.titleDe ?? '',
          savedU.descriptionDe ?? '',
          savedU.titleEn,
          savedU.descriptionEn,
        ),
        duration: savedU.duration ?? 0,
        sortOrder: savedU.sortOrder ?? 0,
      }))
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }
}
